import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ROUTES } from '@/config/routes.ts';
import { organizerSidebar } from '@/config/sidebarConfig.ts';
import {
  createCompetitionApi,
  createDraftCompetitionApi,
  publishCompetitionApi,
  uploadCompetitionCoverApi,
  upsertCompetitionRuleApi,
} from '@/lib/api/competitionsApi';
import type { CreateCompetitionDraftPayload } from '@/lib/api/competitionsApi';
import { getOrganizationApi } from '@/lib/api/organizationsApi';
import { buildPublishConfirmMessage, publishDateErrors, reminderSkipWarning } from '@/lib/competition-messages';
import { clampLocalDateTime, localDateTimeInputToUtcIso } from '@/lib/date-input';
import { queryKeys } from '@/lib/query/queryKeys';
import {
  BASE_INPUT_CLASS,
  type CompetitionFormData,
  type CompetitionFormErrors,
  CompetitionFormSchema,
  type CompetitionFormState,
  LEVEL_OPTIONS,
  PLAYERS_PER_TYPE,
  SPORT_OPTIONS,
  STRIPE_FIXED_CENTS,
  STRIPE_RATE_BPS,
  TYPE_OPTIONS,
  validateCompetitionDraftMinimal,
  validateCompetitionField,
} from '@/lib/validation/competitionSchema';
import { useAuthStore } from '@/stores/auth-store';
import { computeCommissionCents, slugify } from '@shared';
import { CompetitionLevel, CompetitionType, SportType, StripeAccountStatus } from '@shared/enums/domain.enum';
import type { AddressSuggestion } from '@shared/types/entities';
import { isOrgaForfaitActive } from '@shared/utils/premium.utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Calendar, Info, ScrollText, Send, Users } from 'lucide-react';
import { toast } from 'sonner';

import { CoverUploadSection } from '../../components/competition/CoverUploadSection';
import { FfpjpFiltersSection } from '../../components/competition/FfpjpFiltersSection';
import { PriceEstimation } from '../../components/competition/PriceEstimation';
import { type DraftRule, RulesEditor } from '../../components/competition/RulesEditor';
import { AddressAutocomplete } from '../../components/geo/AddressAutocomplete';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { LeafletMap } from '../../components/map/mapLeaflet';

const initialForm: CompetitionFormState = {
  name: '',
  alias: '',
  type: '',
  level: '',
  sportType: SportType.PETANQUE,
  date: '',
  registrationEnd: '',
  registrationOpensAt: '',
  maxTeams: '',
  minTeams: '',
  playersPerTeam: 3,
  price: '',
  venue: '',
  description: '',
  restrictedToMembers: false,
  restrictedAffiliates: false,
  requireTeamName: true,
  addressLabel: '',
  city: '',
  department: '',
  postcode: '',
  citycode: '',
  latitude: null,
  longitude: null,
  allowedAgeCategories: [],
  allowedClassifications: [],
};

const DEFAULT_COMPETITION_TIME = '09:00';
const baseInputClass = 'w-full rounded-lg border px-4 py-3 focus:border-blue-500 focus:outline-none';

function getDatePart(dateTime: string): string {
  return dateTime.split('T')[0] ?? '';
}

function getTimePart(dateTime: string): string {
  return dateTime.includes('T')
    ? (dateTime.split('T')[1]?.slice(0, 5) ?? DEFAULT_COMPETITION_TIME)
    : DEFAULT_COMPETITION_TIME;
}

function combineDateAndTime(date: string, time: string): string {
  return date ? `${date}T${time || DEFAULT_COMPETITION_TIME}` : '';
}

// ─── Component ────────────────────────────────────────────
export default function CompetitionCreate() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const organizationId = user?.organizationsAdmin[0]?.organizationId;

  const { data: orgData } = useQuery({
    queryKey: queryKeys.organizations.detail(organizationId ?? 0),
    queryFn: () => getOrganizationApi(organizationId!),
    enabled: !!organizationId && isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });

  const [form, setForm] = useState<CompetitionFormState>(initialForm);
  const [aliasManual, setAliasManual] = useState(false);
  const [errors, setErrors] = useState<CompetitionFormErrors>({});
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [rules, setRules] = useState<DraftRule[]>([]);

  const authOrg = user?.organizationsAdmin[0]?.organization;
  const forfaitActive = isOrgaForfaitActive(authOrg?.plan, authOrg?.subscriptionStatus);

  const priceCents = form.price !== '' ? Math.round(Number(form.price) * 100) : 0;
  const isCommission = !forfaitActive && priceCents > 0;
  const bcFeeCents = isCommission ? computeCommissionCents(priceCents) : 0;
  const stripeFeeCents = priceCents > 0 ? Math.round((priceCents * STRIPE_RATE_BPS) / 10_000) + STRIPE_FIXED_CENTS : 0;
  const netOrgaCents = Math.max(0, priceCents - bcFeeCents - stripeFeeCents);
  const stripeNotActive = orgData?.stripeAccountStatus !== StripeAccountStatus.ACTIVE;

  // ─── Helpers ──────────────────────────────────────────
  function set<K extends keyof CompetitionFormState>(key: K, value: CompetitionFormState[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  function fieldClass(key: keyof CompetitionFormErrors) {
    return `${BASE_INPUT_CLASS} ${errors[key] ? 'border-red-400 bg-red-50' : 'border-slate-200'}`;
  }

  function handleBlur(key: keyof CompetitionFormData, value: unknown) {
    const msg = validateCompetitionField(key, value);
    setErrors(prev => ({ ...prev, [key]: msg }));

    // Cohérence cross-field : la clôture est cappée à l'instant de début du
    // concours (datetime). Si l'utilisateur saisit une clôture après le coup
    // d'envoi (même jour ou plus tard), on la ramène à l'heure du concours.
    if (key === 'registrationEnd' || key === 'date') {
      const regEndVal = key === 'registrationEnd' ? String(value) : form.registrationEnd;
      const compDateVal = key === 'date' ? String(value) : form.date;

      if (regEndVal && compDateVal) {
        const capped = clampLocalDateTime(regEndVal, compDateVal);
        setErrors(prev => ({ ...prev, registrationEnd: undefined }));
        if (capped !== regEndVal) {
          set('registrationEnd', capped);
          toast.info('Clôture des inscriptions ajustée à l’heure de début du concours');
        }
      }
    }
  }

  function handleNameChange(name: string) {
    set('name', name);
    if (!aliasManual) set('alias', slugify(name));
  }

  function handleAliasChange(alias: string) {
    setAliasManual(true);
    set('alias', alias);
  }

  function handleTypeChange(type: CompetitionType) {
    set('type', type);
    set('playersPerTeam', PLAYERS_PER_TYPE[type]);
  }

  function handleCoverChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  }

  function buildPayload() {
    if (!form.type || !form.level || !organizationId) return null;
    return {
      name: form.name.trim(),
      alias: form.alias.trim(),
      type: form.type,
      level: form.level,
      sportType: form.sportType,
      date: localDateTimeInputToUtcIso(form.date),
      registrationEnd: localDateTimeInputToUtcIso(form.registrationEnd),
      ...(form.registrationOpensAt && {
        registrationOpensAt: localDateTimeInputToUtcIso(form.registrationOpensAt),
      }),
      maxTeams: Number(form.maxTeams),
      minTeams: form.minTeams !== '' ? Number(form.minTeams) : undefined,
      playersPerTeam: form.playersPerTeam,
      price: form.price !== '' ? Math.round(Number(form.price) * 100) : undefined,
      venue: form.venue.trim(),
      address: form.addressLabel.trim() || undefined,
      description: form.description.trim() || undefined,
      restrictedToMembers: form.restrictedToMembers,
      restrictedAffiliates: form.restrictedAffiliates,
      requireTeamName: form.requireTeamName,
      organizationId,
      city: form.city || undefined,
      department: form.department || undefined,
      postcode: form.postcode || undefined,
      citycode: form.citycode || undefined,
      latitude: form.latitude ?? undefined,
      longitude: form.longitude ?? undefined,
      allowedAgeCategories: form.allowedAgeCategories,
      allowedClassifications: form.allowedClassifications,
    };
  }

  // Payload partiel pour brouillon : on n'envoie que ce qui est rempli.
  function buildDraftPayload(orgId: number): CreateCompetitionDraftPayload {
    return {
      organizationId: orgId,
      name: form.name.trim(),
      ...(form.alias.trim() && { alias: form.alias.trim() }),
      ...(form.type && { type: form.type }),
      ...(form.level && { level: form.level }),
      sportType: form.sportType,
      ...(form.date && { date: localDateTimeInputToUtcIso(form.date) }),
      ...(form.registrationEnd && {
        registrationEnd: localDateTimeInputToUtcIso(form.registrationEnd),
      }),
      ...(form.registrationOpensAt && {
        registrationOpensAt: localDateTimeInputToUtcIso(form.registrationOpensAt),
      }),
      ...(form.maxTeams !== '' && { maxTeams: Number(form.maxTeams) }),
      ...(form.minTeams !== '' && { minTeams: Number(form.minTeams) }),
      ...(form.type && { playersPerTeam: form.playersPerTeam }),
      ...(form.price !== '' && { price: Math.round(Number(form.price) * 100) }),
      ...(form.venue.trim() && { venue: form.venue.trim() }),
      ...(form.description.trim() && { description: form.description.trim() }),
      restrictedToMembers: form.restrictedToMembers,
      restrictedAffiliates: form.restrictedAffiliates,
      requireTeamName: form.requireTeamName,
      ...(form.city && { city: form.city }),
      ...(form.department && { department: form.department }),
      ...(form.postcode && { postcode: form.postcode }),
      ...(form.citycode && { citycode: form.citycode }),
      ...(form.latitude !== null && { latitude: form.latitude }),
      ...(form.longitude !== null && { longitude: form.longitude }),
    };
  }

  function validate(): boolean {
    const result = CompetitionFormSchema.safeParse(form);
    if (!result.success) {
      const fieldErrors = result.error.flatten().fieldErrors;
      const newErrors: CompetitionFormErrors = {};
      for (const [k, msgs] of Object.entries(fieldErrors)) {
        if (msgs?.[0]) newErrors[k as keyof CompetitionFormErrors] = msgs[0];
      }
      setErrors(newErrors);
      toast.error('Veuillez corriger les erreurs avant de continuer');
      return false;
    }
    setErrors({});
    return true;
  }

  function handleSubmit(intent: 'draft' | 'publish') {
    if (intent === 'draft') {
      // Brouillon : validation minimale (name seulement).
      const draftErrors = validateCompetitionDraftMinimal({ name: form.name });
      if (Object.keys(draftErrors).length > 0) {
        setErrors(prev => ({ ...prev, ...draftErrors }));
        toast.error('Le nom du concours est requis (3 caractères minimum)');
        return;
      }
      draftMutation.mutate();
    } else {
      // Publier : validation complète (tous les champs requis).
      if (!validate()) return;
      // Garde « dans le futur » spécifique à la publication (miroir API).
      const dateErrors = publishDateErrors(form.date, form.registrationEnd);
      if (dateErrors.date || dateErrors.registrationEnd) {
        setErrors(prev => ({ ...prev, ...dateErrors }));
        toast.error(dateErrors.date ?? dateErrors.registrationEnd);
        return;
      }
      if (!window.confirm(buildPublishConfirmMessage(form.date))) return;
      publishMutation.mutate();
    }
  }

  async function upsertRules(competitionId: number) {
    const filledRules = rules.filter(r => r.content.trim());
    for (let i = 0; i < filledRules.length; i++) {
      const r = filledRules[i];
      await upsertCompetitionRuleApi(competitionId, {
        content: r.content,
        type: r.type,
        isVisible: r.isVisible,
        order: i + 1,
      });
    }
  }

  const draftMutation = useMutation({
    mutationFn: async () => {
      if (!organizationId) throw new Error('Aucune organisation associée');
      const payload = buildDraftPayload(organizationId);
      const competition = await createDraftCompetitionApi(payload);
      if (coverFile) await uploadCompetitionCoverApi(competition.id, coverFile);
      await upsertRules(competition.id);
      return competition;
    },
    onSuccess: () => {
      toast.success('Concours enregistré en brouillon');
      void queryClient.invalidateQueries({ queryKey: queryKeys.competitions.all() });
      void queryClient.invalidateQueries({ queryKey: ['org-capabilities', organizationId] });
      navigate(ROUTES.COMPETITION_LIST);
    },
  });

  const publishMutation = useMutation({
    mutationFn: async () => {
      const payload = buildPayload();
      if (!payload) throw new Error('Formulaire incomplet');
      // 1. Création (status DRAFT par défaut côté server)
      const competition = await createCompetitionApi(payload);
      if (coverFile) await uploadCompetitionCoverApi(competition.id, coverFile);
      await upsertRules(competition.id);
      // 2. Publication via endpoint dédié (validation gated des champs requis)
      return publishCompetitionApi(competition.id);
    },
    onSuccess: () => {
      toast.success('Concours publié — inscriptions ouvertes');
      void queryClient.invalidateQueries({ queryKey: queryKeys.competitions.all() });
      void queryClient.invalidateQueries({ queryKey: ['org-capabilities', organizationId] });
      navigate(ROUTES.COMPETITION_LIST);
    },
  });

  const isPending = draftMutation.isPending || publishMutation.isPending;

  // Avertissement rappels Temporal : si la date est < 7j (J-7) ou < 1j (J-1), le
  // workflow skippera le(s) rappel(s) email. Recalculé à chaque rendu (dépend de now).
  const dateReminderWarning = reminderSkipWarning(form.date);

  // ─── Unsaved-changes guard ─────────────────────────────
  const isDirty = useMemo(() => {
    const formDirty = (Object.keys(initialForm) as (keyof CompetitionFormState)[]).some(
      k => form[k] !== initialForm[k],
    );
    return formDirty || rules.length > 0 || coverFile !== null;
  }, [form, rules, coverFile]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  if (!organizationId) {
    return (
      <DashboardLayout sidebarConfig={organizerSidebar}>
        <div className='flex min-h-screen flex-col items-center justify-center gap-4 p-6'>
          <p className='text-red-600'>Aucune organisation associée à votre compte.</p>
          <Link to={ROUTES.COMPETITION_LIST} className='text-sm text-slate-600 underline'>
            Retour à la liste
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout sidebarConfig={organizerSidebar}>
      <div className='p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto'>
        {/* Header */}
        <div className='mb-6 sm:mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
          <div className='flex items-start gap-3 sm:gap-4 min-w-0'>
            <Link
              to={ROUTES.COMPETITION_LIST}
              className='rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 shrink-0'
              onClick={e => {
                if (
                  isDirty &&
                  !window.confirm('Vous avez des modifications non sauvegardées. Quitter sans sauvegarder ?')
                )
                  e.preventDefault();
              }}
              aria-label='Retour'
            >
              <ArrowLeft className='h-5 w-5' />
            </Link>

            <div className='min-w-0'>
              <h1 className='text-2xl sm:text-3xl font-extrabold text-slate-900'>Créer un concours</h1>
              <p className='mt-1 text-slate-500'>Configurez votre nouvel événement</p>
            </div>
          </div>

          <div className='flex flex-col sm:flex-row gap-3 w-full sm:w-auto'>
            <button
              type='button'
              disabled={isPending}
              onClick={() => handleSubmit('draft')}
              className='w-full sm:w-auto rounded-xl border border-slate-200 px-6 py-2 text-sm font-semibold text-slate-700 transition hover:border-blue-300 disabled:opacity-50'
            >
              {draftMutation.isPending ? 'Enregistrement…' : 'Enregistrer en brouillon'}
            </button>

            <button
              type='button'
              disabled={isPending}
              onClick={() => handleSubmit('publish')}
              className='w-full sm:w-auto flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-2 text-sm font-bold text-white shadow-lg shadow-blue-200 transition hover:bg-blue-700 disabled:opacity-50'
            >
              <Send className='h-4 w-4' />
              {publishMutation.isPending ? 'Publication…' : 'Publier'}
            </button>
          </div>
        </div>

        {/* Form */}
        <div className='space-y-6 sm:space-y-8'>
          {/* Section 1 — Informations de base */}
          <div className='rounded-2xl border border-slate-200 bg-white p-4 sm:p-8'>
            <h2 className='mb-6 flex items-center gap-2 text-lg font-bold text-slate-900'>
              <Info className='h-5 w-5 text-blue-600' />
              Informations de base
            </h2>

            <div className='space-y-6'>
              <div>
                <label className='mb-2 block text-sm font-semibold text-slate-700'>
                  Nom du concours <span className='text-red-600'>*</span>
                </label>
                <input
                  type='text'
                  value={form.name}
                  onChange={e => handleNameChange(e.target.value)}
                  onBlur={e => handleBlur('name', e.target.value)}
                  placeholder='Ex: Concours National de Lyon'
                  className={fieldClass('name')}
                />
                {errors.name && <p className='mt-1 text-xs text-red-500'>{errors.name}</p>}
              </div>

              <div>
                <label className='mb-2 block text-sm font-semibold text-slate-700'>
                  Alias (URL) <span className='text-red-600'>*</span>
                </label>
                <input
                  type='text'
                  value={form.alias}
                  onChange={e => handleAliasChange(e.target.value)}
                  onBlur={e => handleBlur('alias', e.target.value)}
                  placeholder='concours-national-lyon'
                  className={fieldClass('alias')}
                />
                {errors.alias ? (
                  <p className='mt-1 text-xs text-red-500'>{errors.alias}</p>
                ) : (
                  <p className='mt-1 text-xs text-slate-400'>Généré automatiquement depuis le nom. Modifiable.</p>
                )}
              </div>

              <div>
                <label className='mb-2 block text-sm font-semibold text-slate-700'>Description</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={e => set('description', e.target.value)}
                  placeholder='Décrivez votre concours...'
                  className={`${baseInputClass} border-slate-200`}
                />
              </div>

              <div className='grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6'>
                <div>
                  <label className='mb-2 block text-sm font-semibold text-slate-700'>
                    Type <span className='text-red-600'>*</span>
                  </label>
                  <select
                    value={form.type}
                    onChange={e => handleTypeChange(e.target.value as CompetitionType)}
                    onBlur={e => handleBlur('type', e.target.value || undefined)}
                    className={fieldClass('type')}
                  >
                    <option value=''>Sélectionner</option>
                    {TYPE_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {errors.type && <p className='mt-1 text-xs text-red-500'>{errors.type}</p>}
                </div>

                <div>
                  <label className='mb-2 block text-sm font-semibold text-slate-700'>
                    Niveau <span className='text-red-600'>*</span>
                  </label>
                  <select
                    value={form.level}
                    onChange={e => set('level', e.target.value as CompetitionLevel)}
                    onBlur={e => handleBlur('level', e.target.value || undefined)}
                    className={fieldClass('level')}
                  >
                    <option value=''>Sélectionner</option>
                    {LEVEL_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {errors.level && <p className='mt-1 text-xs text-red-500'>{errors.level}</p>}
                </div>

                <div>
                  <label className='mb-2 block text-sm font-semibold text-slate-700'>
                    Sport <span className='text-red-600'>*</span>
                  </label>
                  <select
                    value={form.sportType}
                    onChange={e => set('sportType', e.target.value as SportType)}
                    className={`${baseInputClass} border-slate-200`}
                  >
                    {SPORT_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2 — Date et lieu */}
          <div className='rounded-2xl border border-slate-200 bg-white p-4 sm:p-8'>
            <h2 className='mb-6 flex items-center gap-2 text-lg font-bold text-slate-900'>
              <Calendar className='h-5 w-5 text-emerald-600' />
              Date et lieu
            </h2>

            <div className='space-y-6'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6'>
                <div>
                  <label className='mb-2 block text-sm font-semibold text-slate-700'>
                    Date et heure du concours <span className='text-red-600'>*</span>
                  </label>
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                    <div>
                      <label className='mb-1 block text-xs font-semibold text-slate-500'>Date</label>
                      <input
                        type='date'
                        value={getDatePart(form.date)}
                        onChange={e => set('date', combineDateAndTime(e.target.value, getTimePart(form.date)))}
                        onBlur={e => handleBlur('date', combineDateAndTime(e.target.value, getTimePart(form.date)))}
                        className={fieldClass('date')}
                      />
                    </div>
                    <div>
                      <label className='mb-1 block text-xs font-semibold text-slate-500'>Heure</label>
                      <input
                        type='time'
                        value={getTimePart(form.date)}
                        onChange={e => set('date', combineDateAndTime(getDatePart(form.date), e.target.value))}
                        onBlur={e => handleBlur('date', combineDateAndTime(getDatePart(form.date), e.target.value))}
                        className={fieldClass('date')}
                      />
                    </div>
                  </div>
                  {errors.date && <p className='mt-1 text-xs text-red-500'>{errors.date}</p>}
                  {!errors.date && dateReminderWarning && (
                    <p className='mt-1 text-xs text-amber-600'>⚠️ {dateReminderWarning}</p>
                  )}
                </div>

                <div>
                  <label className='mb-2 block text-sm font-semibold text-slate-700'>
                    Clôture des inscriptions <span className='text-red-600'>*</span>
                  </label>
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                    <div>
                      <label className='mb-1 block text-xs font-semibold text-slate-500'>Date</label>
                      <input
                        type='date'
                        value={getDatePart(form.registrationEnd)}
                        onChange={e =>
                          set('registrationEnd', combineDateAndTime(e.target.value, getTimePart(form.registrationEnd)))
                        }
                        onBlur={e =>
                          handleBlur(
                            'registrationEnd',
                            combineDateAndTime(e.target.value, getTimePart(form.registrationEnd)),
                          )
                        }
                        className={fieldClass('registrationEnd')}
                      />
                    </div>
                    <div>
                      <label className='mb-1 block text-xs font-semibold text-slate-500'>Heure</label>
                      <input
                        type='time'
                        value={getTimePart(form.registrationEnd)}
                        onChange={e =>
                          set('registrationEnd', combineDateAndTime(getDatePart(form.registrationEnd), e.target.value))
                        }
                        onBlur={e =>
                          handleBlur(
                            'registrationEnd',
                            combineDateAndTime(getDatePart(form.registrationEnd), e.target.value),
                          )
                        }
                        className={fieldClass('registrationEnd')}
                      />
                    </div>
                  </div>
                  {errors.registrationEnd && <p className='mt-1 text-xs text-red-500'>{errors.registrationEnd}</p>}
                </div>
              </div>

              {/* Feature 7 (2026-08) — date d'ouverture des inscriptions (optionnelle) */}
              <div>
                <label className='mb-2 block text-sm font-semibold text-slate-700'>
                  Date d&apos;ouverture des inscriptions <span className='font-normal text-slate-400'>(optionnel)</span>
                </label>
                <input
                  type='date'
                  value={getDatePart(form.registrationOpensAt)}
                  onChange={e => set('registrationOpensAt', e.target.value ? `${e.target.value}T00:01` : '')}
                  onBlur={e => handleBlur('registrationOpensAt', e.target.value ? `${e.target.value}T00:01` : '')}
                  className={fieldClass('registrationOpensAt')}
                />
                <p className='mt-1 text-xs text-amber-600'>
                  ⚠️ Si renseignée, les inscriptions resteront <strong>bloquées jusqu&apos;à cette date</strong> (à
                  00h01), même si le concours est publié. Laissez vide pour ouvrir les inscriptions dès la publication.
                </p>
                {errors.registrationOpensAt && (
                  <p className='mt-1 text-xs text-red-500'>{errors.registrationOpensAt}</p>
                )}
              </div>

              <div className='space-y-3'>
                <div>
                  <label className='mb-2 block text-sm font-semibold text-slate-700'>
                    Nom du lieu <span className='text-red-600'>*</span>
                  </label>
                  <input
                    type='text'
                    value={form.venue}
                    onChange={e => set('venue', e.target.value)}
                    onBlur={e => handleBlur('venue', e.target.value)}
                    placeholder='Ex: Boulodrome Municipal de Lyon'
                    className={fieldClass('venue')}
                  />
                  {errors.venue && <p className='mt-1 text-xs text-red-500'>{errors.venue}</p>}
                </div>

                <div>
                  <label className='mb-2 block text-sm font-semibold text-slate-700'>
                    Adresse (pour la géolocalisation)
                  </label>
                  <AddressAutocomplete
                    value={form.addressLabel}
                    onChange={v => set('addressLabel', v)}
                    onSelect={(s: AddressSuggestion) => {
                      setForm(prev => ({
                        ...prev,
                        addressLabel: s.label,
                        city: s.city ?? '',
                        department: s.context?.split(', ')[1] ?? '',
                        postcode: s.postcode ?? '',
                        citycode: s.citycode ?? '',
                        latitude: s.latitude,
                        longitude: s.longitude,
                      }));
                    }}
                    placeholder="Rechercher l'adresse du lieu…"
                  />
                  {form.latitude && form.longitude && (
                    <p className='mt-1 text-xs text-emerald-600'>
                      📍 {form.city} ({form.postcode}) — {form.latitude.toFixed(5)}, {form.longitude.toFixed(5)}
                    </p>
                  )}
                </div>
              </div>

              <div className='rounded-3xl border border-slate-200 bg-white p-4 sm:p-6'>
                <div className='relative flex aspect-video items-center justify-center rounded-2xl bg-linear-to-br from-slate-200 to-slate-300 overflow-hidden'>
                  <LeafletMap
                    lat={form.latitude ? form.latitude : undefined}
                    lng={form.longitude ? form.longitude : undefined}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section 3 — Inscriptions */}
          <div className='rounded-2xl border border-slate-200 bg-white p-4 sm:p-8'>
            <h2 className='mb-6 flex items-center gap-2 text-lg font-bold text-slate-900'>
              <Users className='h-5 w-5 text-purple-600' />
              Inscriptions
            </h2>

            <div className='space-y-6'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6'>
                <div>
                  <label className='mb-2 block text-sm font-semibold text-slate-700'>
                    Équipes max <span className='text-red-600'>*</span>
                  </label>
                  <input
                    type='number'
                    min={2}
                    value={form.maxTeams}
                    onChange={e => set('maxTeams', e.target.value)}
                    onBlur={e => handleBlur('maxTeams', e.target.value)}
                    placeholder='Ex: 64'
                    className={fieldClass('maxTeams')}
                  />
                  {errors.maxTeams && <p className='mt-1 text-xs text-red-500'>{errors.maxTeams}</p>}
                </div>

                <div>
                  <label className='mb-2 block text-sm font-semibold text-slate-700'>Équipes min</label>
                  <input
                    type='number'
                    min={2}
                    value={form.minTeams}
                    onChange={e => set('minTeams', e.target.value)}
                    onBlur={e => handleBlur('minTeams', e.target.value)}
                    placeholder='Ex: 8'
                    className={fieldClass('minTeams')}
                  />
                  {errors.minTeams ? (
                    <p className='mt-1 text-xs text-red-500'>{errors.minTeams}</p>
                  ) : (
                    <p className='mt-1 text-xs text-slate-400'>Facultatif</p>
                  )}
                </div>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6'>
                <div>
                  <label className='mb-2 block text-sm font-semibold text-slate-700'>Joueurs par équipe</label>
                  <input
                    type='number'
                    value={form.playersPerTeam}
                    readOnly
                    className='w-full cursor-not-allowed rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-slate-500'
                  />
                  <p className='mt-1 text-xs text-slate-400'>Déduit du type de concours</p>
                </div>

                <div>
                  <label className='mb-2 block text-sm font-semibold text-slate-700'>Prix par équipe (€)</label>
                  <div className='relative'>
                    <input
                      type='number'
                      min={0}
                      step='0.01'
                      value={form.price}
                      onChange={e => set('price', e.target.value)}
                      onBlur={e => handleBlur('price', e.target.value)}
                      placeholder='Ex: 30'
                      className={fieldClass('price')}
                    />
                    <span className='absolute right-4 top-1/2 -translate-y-1/2 font-semibold text-slate-400'>€</span>
                  </div>

                  {errors.price ? (
                    <p className='mt-1 text-xs text-red-500'>{errors.price}</p>
                  ) : (
                    <p className='mt-1 text-xs text-slate-400'>Laisser vide pour gratuit</p>
                  )}

                  {priceCents > 0 && stripeNotActive && (
                    <div className='mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3'>
                      <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-amber-500' />
                      <p className='text-xs text-amber-800'>
                        Votre compte Stripe n&apos;est pas encore actif. Les joueurs ne pourront pas payer pour ce
                        concours. Activez votre compte Stripe dans le tableau de bord.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <PriceEstimation
                priceCents={priceCents}
                isCommission={isCommission}
                bcFeeCents={bcFeeCents}
                stripeFeeCents={stripeFeeCents}
                netOrgaCents={netOrgaCents}
              />

              <div className='flex flex-col md:flex-row gap-2'>
                {/* Demande client #1 — nom d'équipe obligatoire ? */}
                <div className='relative flex items-center gap-2 group'>
                  <input
                    type='checkbox'
                    id='requireTeamName'
                    checked={form.requireTeamName}
                    onChange={e => set('requireTeamName', e.target.checked)}
                    className='h-5 w-5 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500 shrink-0'
                  />
                  <label htmlFor='requireTeamName' className='cursor-pointer text-sm font-semibold text-slate-700'>
                    Nom d&apos;équipe obligatoire à l&apos;inscription
                  </label>
                  <span className='pointer-events-none absolute left-0 bottom-full mb-2 w-80 rounded-md bg-slate-800 px-3 py-2 text-xs text-white shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-20'>
                    Décochez pour permettre l&apos;inscription sans nom d&apos;équipe (un nom sera généré
                    automatiquement). À venir : les capitaines pourront enregistrer leur équipe favorite pour
                    pré-remplir automatiquement leurs futures inscriptions.
                  </span>
                </div>

                <div className='relative flex items-center gap-2 group'>
                  <input
                    type='checkbox'
                    id='restricted'
                    checked={form.restrictedToMembers}
                    onChange={e => set('restrictedToMembers', e.target.checked)}
                    className='h-5 w-5 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500 shrink-0'
                  />
                  <label htmlFor='restricted' className='cursor-pointer text-sm font-semibold text-slate-700'>
                    Réservé aux membres licenciés
                  </label>
                  <span className='pointer-events-none absolute left-0 bottom-full mb-2 w-72 rounded-md bg-slate-800 px-3 py-2 text-xs text-white shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-20'>
                    Seuls les joueurs avec une licence fédérale valide peuvent s&apos;inscrire à ce concours.
                  </span>
                </div>

                <div className='relative flex items-center gap-2 group'>
                  <input
                    type='checkbox'
                    id='restrictedAffiliate'
                    checked={form.restrictedAffiliates}
                    onChange={e => set('restrictedAffiliates', e.target.checked)}
                    className='h-5 w-5 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500 shrink-0'
                  />
                  <label htmlFor='restrictedAffiliate' className='cursor-pointer text-sm font-semibold text-slate-700'>
                    Réservé aux membres des clubs affiliés
                  </label>
                  <span className='pointer-events-none absolute left-0 bottom-full mb-2 w-72 rounded-md bg-slate-800 px-3 py-2 text-xs text-white shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-20'>
                    L&apos;inscription est réservée aux joueurs membres d&apos;un club affilié à votre organisation.
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Section FFPJP — Filtres fédéraux */}
          <FfpjpFiltersSection
            allowedAgeCategories={form.allowedAgeCategories}
            allowedClassifications={form.allowedClassifications}
            onAgeCategoriesChange={cats => set('allowedAgeCategories', cats)}
            onClassificationsChange={cls => set('allowedClassifications', cls)}
          />

          {/* Section 4 — Règles du concours */}
          <div className='rounded-2xl border border-slate-200 bg-white p-4 sm:p-8'>
            <h2 className='mb-2 flex items-center gap-2 text-lg font-bold text-slate-900'>
              <ScrollText className='h-5 w-5 text-indigo-600' />
              Règles du concours
            </h2>
            <p className='mb-6 text-sm text-slate-500'>
              Affichées aux joueurs sur la page d&apos;inscription publique. Vous pouvez masquer une règle sans la
              supprimer (icône œil).
            </p>
            <RulesEditor rules={rules} onChange={setRules} />
          </div>

          {/* Section 5 — Affiche du concours */}
          <CoverUploadSection coverPreview={coverPreview} coverFile={coverFile} onCoverChange={handleCoverChange} />

          {/* Bottom actions */}
          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-6'>
            <Link
              to={ROUTES.COMPETITION_LIST}
              className='w-full sm:w-auto px-6 py-3 text-sm font-semibold text-slate-700 transition hover:text-slate-900 text-center'
              onClick={e => {
                if (
                  isDirty &&
                  !window.confirm('Vous avez des modifications non sauvegardées. Quitter sans sauvegarder ?')
                )
                  e.preventDefault();
              }}
            >
              Annuler
            </Link>

            <div className='flex flex-col sm:flex-row gap-3 w-full sm:w-auto'>
              <button
                type='button'
                disabled={isPending}
                onClick={() => handleSubmit('draft')}
                className='w-full sm:w-auto rounded-xl border border-slate-200 px-6 py-3 text-sm font-semibold text-slate-700 transition hover:border-blue-300 disabled:opacity-50'
              >
                {draftMutation.isPending ? 'Enregistrement…' : 'Enregistrer en brouillon'}
              </button>
              <button
                type='button'
                disabled={isPending}
                onClick={() => handleSubmit('publish')}
                className='w-full sm:w-auto flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-blue-200 transition hover:bg-blue-700 disabled:opacity-50'
              >
                <Send className='h-4 w-4' />
                {publishMutation.isPending ? 'Publication…' : 'Publier le concours'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
